import React, { useEffect } from "react";
import { Form, InputNumber, Tooltip } from "antd";
import styled from "styled-components";
import { InfoCircleOutlined } from "@ant-design/icons";

interface Props {
  form: any;
  formData: any;
  setFormData: any;
}

const BureauxPricesList: React.FC<Props> = ({
  form,
  formData,
  setFormData,
}) => {
  const bureaux = form.getFieldValue("bureaux") || [];

  useEffect(() => {
    const currentPrices = form.getFieldValue("bureaux_prices") || [];
    if (currentPrices.length !== bureaux.length) {
      const newPrices = bureaux.map((apt: any, index: number) => {
        return (
          currentPrices[index] || {
            prix_vente_unitaire_min: null,
            prix_vente_unitaire_max: null,
            prix_vente_min: null,
            prix_vente_max: null,
            prix_loyer_min: null,
            prix_loyer_max: null,
            nbr_unites_total: null,
            nbr_unites_vendues: null,
            nbr_unites_invendues: null,
          }
        );
      });
      form.setFieldsValue({ bureaux_prices: newPrices });
      setFormData((prev: any) => ({
        ...prev,
        bureaux_prices: newPrices,
      }));
    }
  }, [bureaux, form, setFormData]);

  const handlePriceChange = (
    index: number,
    field: string,
    value: number | null | boolean
  ) => {
    const currentPrices = form.getFieldValue("bureaux_prices") || [];
    const bureaux = form.getFieldValue("bureaux") || [];
    const currentBureaux = bureaux[index] || {};
    
    const updatedPrices = currentPrices.map((price: any, idx: number) => {
      if (idx === index) {
        // Copy the current price object to avoid mutation
        let updatedPrice = { ...price };
        
      
       if (field === "nbr_unites_total") {
          // If total is set to null, force vendus and invendues to null as well.
          if (value === null) {
            updatedPrice = {
              ...updatedPrice,
              nbr_unites_total: null,
              nbr_unites_vendues: null,
              nbr_unites_invendues: null,
            };
          } else {
            const vendus = updatedPrice.nbr_unites_vendues ?? 0;
            const invendues = Math.max(0, Number(value) - Number(vendus));
            updatedPrice = {
              ...updatedPrice,
              nbr_unites_total: value,
              nbr_unites_invendues: invendues,
              nbr_unites_vendues: vendus,
            };
          }
        } else if (field === "nbr_unites_vendues") {
          const total = updatedPrice.nbr_unites_total || 0;
          const invendues =
            value !== null ? Math.max(0, Number(total) - Number(value)) : null;
          updatedPrice = { ...updatedPrice, [field]: value, nbr_unites_invendues: invendues };
        } else if (field === "nbr_unites_invendues") {
          const total = updatedPrice.nbr_unites_total || 0;
          const vendus =
            value !== null ? Math.max(0, Number(total) - Number(value)) : null;
          updatedPrice = { ...updatedPrice, [field]: value, nbr_unites_vendues: vendus };
        } else {
          // Standard field update
          updatedPrice = { ...updatedPrice, [field]: value };
          
          // Get surface values
          const surfaceMin = currentBureaux.bureau_surface_min;
          const surfaceMax = currentBureaux.bureau_surface_max;
          
          // Automatic price calculations based on which field changed
          if (field === "prix_vente_unitaire_min" && surfaceMin) {
            // Calculate total price min from unit price min * surface min
            updatedPrice.prix_vente_min = Math.round(Number(value) * surfaceMin);
          } 
          else if (field === "prix_vente_unitaire_max" && surfaceMax) {
            // Calculate total price max from unit price max * surface max
            updatedPrice.prix_vente_max = Math.round(Number(value) * surfaceMax);
          }
          else if (field === "prix_vente_min" && surfaceMin) {
            // Calculate unit price min from total price min / surface min
            updatedPrice.prix_vente_unitaire_min = Math.round(Number(value) / surfaceMin);
          }
          else if (field === "prix_vente_max" && surfaceMax) {
            // Calculate unit price max from total price max / surface max
            updatedPrice.prix_vente_unitaire_max = Math.round(Number(value) / surfaceMax);
          }
        }
        
        return updatedPrice;
      }
      return price;
    });
    
    form.setFieldsValue({ bureaux_prices: updatedPrices });
    setFormData((prev: any) => ({
      ...prev,
      bureaux_prices: updatedPrices,
    }));
  };

  return (
    <ScrollableTableWrapper>
      <table
        style={{
          minWidth: "800px",
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "2px solid #42005a",
                padding: "3px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#42005a",
                backgroundColor: "#eeedf0",
                borderBottom: "none",
              }}
            >
              Type
            </th>
            <th style={headerStyle} colSpan={3}>
              Unités
            </th>
            <th style={headerStyle} colSpan={2}>
              Prix Vente Unitaire (MAD/m2)
            </th>
            <th style={headerStyle} colSpan={2}>
              Prix Vente (MAD)<Tooltip placement="top" title="Calcul effectué : Surface * Prix vente unitaire" color={"#42005a"}><InfoCircleOutlined /> </Tooltip>
            </th>
            <th style={headerStyle} colSpan={2}>
              Prix Loyer (MAD)
            </th>
          </tr>
          <tr>
            <th
              style={{
                border: "2px solid #42005a",
                padding: "3px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#42005a",
                backgroundColor: "#eeedf0",
                borderTop: "none",
              }}
            ></th>
            <th style={subHeaderStyle}>Total</th>
            <th style={subHeaderStyle}>Vendus</th>
            <th style={subHeaderStyle}>Invendues</th>
            <th style={subHeaderStyle}>Min</th>
            <th style={subHeaderStyle}>Max</th>
            <th style={subHeaderStyle}>Min</th>
            <th style={subHeaderStyle}>Max</th>
            <th style={subHeaderStyle}>Min</th>
            <th style={subHeaderStyle}>Max</th>
          </tr>
        </thead>
        <tbody>
          {bureaux.map((bureau: any, index: number) => (
            <tr key={index}>
              <td style={cellStyle}>{bureau?.name || "N/A"}</td>
              {/* New section for units */}
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "nbr_unites_total"]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      handlePriceChange(index, "nbr_unites_total", value)
                    }
                  />
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "nbr_unites_vendues"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const totalUnits = getFieldValue([
                          "bureaux_prices",
                          index,
                          "nbr_unites_total",
                        ]);
                        if (!value || !totalUnits || value <= totalUnits) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    disabled={
                      !form.getFieldValue([
                        "bureaux_prices",
                        index,
                        "nbr_unites_total",
                      ])
                    }
                    onChange={(value) =>
                      handlePriceChange(index, "nbr_unites_vendues", value)
                    }
                  />
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "nbr_unites_invendues"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const totalUnits = getFieldValue([
                          "bureaux_prices",
                          index,
                          "nbr_unites_total",
                        ]);
                        if (!value || !totalUnits || value <= totalUnits) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    disabled={
                      !form.getFieldValue([
                        "bureaux_prices",
                        index,
                        "nbr_unites_total",
                      ])
                    }
                    onChange={(value) =>
                      handlePriceChange(index, "nbr_unites_invendues", value)
                    }
                  />
                </Form.Item>
              </td>

              {/* Prix Vente Unitaire Min */}
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "prix_vente_unitaire_min"]}
                  rules={[
                    { required: true, message: "" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const maxPrice = getFieldValue([
                          "bureaux_prices",
                          index,
                          "prix_vente_unitaire_max",
                        ]);
                        if (!value || !maxPrice || value < maxPrice) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      handlePriceChange(index, "prix_vente_unitaire_min", value)
                    }
                  />
                </Form.Item>
              </td>
              {/* Prix Vente Unitaire Max */}
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "prix_vente_unitaire_max"]}
                  rules={[
                    { required: true, message: "" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const minPrice = getFieldValue([
                          "bureaux_prices",
                          index,
                          "prix_vente_unitaire_min",
                        ]);
                        if (!value || !minPrice || value > minPrice) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      handlePriceChange(index, "prix_vente_unitaire_max", value)
                    }
                  />
                </Form.Item>
              </td>
              {/* Prix Vente Min */}
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "prix_vente_min"]}
                  rules={[
                    { required: true, message: "" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const maxPrice = getFieldValue([
                          "bureaux_prices",
                          index,
                          "prix_vente_max",
                        ]);
                        if (!value || !maxPrice || value < maxPrice) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      handlePriceChange(index, "prix_vente_min", value)
                    }
                  />
                </Form.Item>
              </td>
              {/* Prix Vente Max */}
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "prix_vente_max"]}
                  rules={[
                    { required: true, message: "" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const minPrice = getFieldValue([
                          "bureaux_prices",
                          index,
                          "prix_vente_min",
                        ]);
                        if (!value || !minPrice || value > minPrice) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      handlePriceChange(index, "prix_vente_max", value)
                    }
                  />
                </Form.Item>
              </td>
              {/* Prix Loyer Min */}
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "prix_loyer_min"]}
                  rules={[
                  
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const maxPrice = getFieldValue([
                          "bureaux_prices",
                          index,
                          "prix_loyer_max",
                        ]);
                        if (!value || !maxPrice || value < maxPrice) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      handlePriceChange(index, "prix_loyer_min", value)
                    }
                  />
                </Form.Item>
              </td>
              {/* Prix Loyer Max */}
              <td style={cellStyle}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={["bureaux_prices", index, "prix_loyer_max"]}
                  rules={[
                 
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const minPrice = getFieldValue([
                          "bureaux_prices",
                          index,
                          "prix_loyer_min",
                        ]);
                        if (!value || !minPrice || value > minPrice) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(""));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      handlePriceChange(index, "prix_loyer_max", value)
                    }
                  />
                </Form.Item>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollableTableWrapper>
  );
};

const headerStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  fontSize: "14px",
  fontWeight: "bold",
  color: "#42005a",
  backgroundColor: "#eeedf0",
};

const subHeaderStyle = {
  ...headerStyle,
  fontSize: "12px",
  borderLeft: "none",
};

const cellStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  textAlign: "center" as const,
};

const ScrollableTableWrapper = styled.div`
  overflow-x: auto;
  padding-bottom: 8px;
  margin-top: 16px;

  &::-webkit-scrollbar {
    height: 16px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #42005a;
    border-radius: 4px;
    &:hover {
      background-color: #603570;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: #f1f1f1;
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: #42005a #f1f1f1;
`;

export default BureauxPricesList;
